import { ENTITY_TYPE_LABEL } from '../chunk-VBCSY26D.js';
export { VALUE_SCENE_KINDS, bridgeHopsFromScene, bridgeNameFromScene, focusBridgeHops, focusMoves, isValueScene, movesFromScene, readTransactionMoney, transitParties, unreadableMoves } from '../chunk-WQRYSN34.js';
import { sagaLabel, titleRole } from '../chunk-XPI3N6I4.js';
import '../chunk-B3WLT2VP.js';
import '../chunk-PCIOFMXB.js';
import '../chunk-7PG254B4.js';
import '../chunk-YA25Q2WS.js';
import '../chunk-O2TUCS6S.js';
import { normalizeAddress, asTrimmedString, isZeroAddress, shortenAddress, shortenIdentifier, EVM_ZERO_ADDRESS, isValidAddress, asString } from '../chunk-T3LQT4VP.js';
export { EVM_ZERO_ADDRESS as ZERO_ADDRESS, isZeroAddress } from '../chunk-T3LQT4VP.js';
import '../chunk-AKIWOWOF.js';
import { getChainMetadata, NATIVE_TOKEN, toCanonicalChainId } from '../chunk-R5ZQC5YM.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';

// src/funds/categories.ts
var NOTABLE_CATEGORIES = Object.freeze([
  "sanctioned",
  "mixer",
  "exchange",
  "cefi",
  "bridge"
]);
var ALARMING_CATEGORIES = Object.freeze([
  "mixer",
  "sanctioned"
]);
var UNKNOWN_CATEGORIES = Object.freeze([
  "unattributed",
  "resolving",
  "unresolved"
]);
var CATEGORY_LABEL = Object.freeze({
  resolving: "Still resolving\u2026",
  ...ENTITY_TYPE_LABEL,
  unattributed: "Unattributed",
  unresolved: "Attribution could not be read",
  followed: "Followed by this case",
  minted: "Minted",
  burned: "Burned",
  wrapped_native: "Wrapped native asset"
});
function counterpartyInfrastructureCategory(address, chainId) {
  const wrapped = getChainMetadata(chainId)?.wrappedNativeAddress;
  return address && wrapped && normalizeAddress(address, chainId) === normalizeAddress(wrapped, chainId) ? "wrapped_native" : null;
}
function unresolvedCategory(counterparty, hasResolver, attributionPending) {
  if (!counterparty) return "unattributed";
  if (attributionPending) return "resolving";
  return hasResolver ? "unresolved" : "unattributed";
}
function categoryLabel(category) {
  return CATEGORY_LABEL[category] ?? category;
}

// src/funds/types.ts
function flowSelectionIds(flow, selection) {
  const buckets = flow?.[selection.direction === "in" ? "source" : "destination"].buckets ?? [];
  if (selection.counterparty) {
    return [...new Set(buckets.flatMap(
      (bucket) => bucket.top.filter((party) => party.address === selection.counterparty).flatMap((party) => party.transactionIds ?? [])
    ))];
  }
  return buckets.find((bucket) => bucket.category === selection.category)?.transactionIds ?? [];
}
var MAX_BUCKET_COUNTERPARTIES = 8;
var EMPTY_SIDE = Object.freeze({
  buckets: [],
  usdTotal: 0,
  unpricedCount: 0,
  transactions: 0
});

// src/funds/sides.ts
function addCounterpartyChain(tally, chain) {
  tally.chains ??= /* @__PURE__ */ new Set();
  tally.chains.add(chain ? toCanonicalChainId(chain) ?? chain : null);
}
function addAsset(into, asset, amount, usd) {
  const seen = into.get(asset.key);
  if (!seen) {
    into.set(asset.key, { asset, amount, usd });
    return;
  }
  seen.amount += amount;
  seen.usd = seen.usd === null || usd === null ? null : seen.usd + usd;
}
function count(value) {
  return typeof value === "number" ? value : value.size;
}
function assetList(tallies) {
  if (!tallies || tallies.size === 0) return void 0;
  return [...tallies.values()].map((t) => ({ asset: t.asset, amount: t.amount, usd: t.usd })).sort((a, b) => (b.usd ?? -1) - (a.usd ?? -1) || b.amount - a.amount);
}
function buildSide(tallies, unpriced, transactions, pinned = []) {
  const rank = new Map(pinned.map((category, index) => [category, index]));
  const pinRank = (category) => rank.get(category) ?? Number.MAX_SAFE_INTEGER;
  let usdTotal = 0;
  for (const tally of tallies.values()) usdTotal += tally.usd;
  const buckets = [...tallies.entries()].map(([category, tally]) => {
    const top = [...tally.counterparties.values()].map((c) => ({
      address: c.address,
      ...c.chains?.size === 1 && !c.chains.has(null) ? { chainId: [...c.chains][0] } : {},
      usd: c.usd,
      transactions: count(c.transactions),
      ...c.transactions instanceof Set ? { transactionIds: [...c.transactions] } : {},
      ...c.assets ? { assets: assetList(c.assets) } : {},
      ...c.remoteChains && c.remoteChains.size > 0 ? { remoteChains: [...c.remoteChains].sort() } : {},
      ...c.protocol !== void 0 ? { protocol: c.protocol } : {}
    })).sort((a, b) => b.usd - a.usd || b.transactions - a.transactions).slice(0, MAX_BUCKET_COUNTERPARTIES);
    const bucket = {
      category,
      usd: tally.usd,
      counterparties: tally.counterparties.size,
      transactions: count(tally.transactions),
      ...tally.transactions instanceof Set ? { transactionIds: [...tally.transactions] } : {},
      // Zero denominator means every priced amount was worth nothing, which
      // is a real state (a feed of contract calls). Shares are then 0, not NaN.
      share: usdTotal > 0 ? tally.usd / usdTotal : 0,
      top
    };
    const assets = assetList(tally.assets);
    if (assets) bucket.assets = assets;
    return bucket;
  }).sort(
    (a, b) => pinRank(a.category) - pinRank(b.category) || b.usd - a.usd || b.transactions - a.transactions
  );
  return { buckets, usdTotal, unpricedCount: unpriced, transactions };
}

// src/funds/txFlow.ts
function normalized(value) {
  return toCanonicalChainId(asTrimmedString(value));
}
function sideAddress(row, side) {
  const address = asTrimmedString(row[side]) ?? asTrimmedString(row[`${side}_address`]);
  return address ? normalizeAddress(address) : null;
}
function emptyFlow() {
  return {
    source: EMPTY_SIDE,
    destination: EMPTY_SIDE,
    internalCount: 0,
    excludedCount: 0,
    droppedUnpricedCount: 0,
    outsideEndsCount: 0,
    tokenValueCount: 0,
    tokenValueUsd: 0,
    tokenValuePendingCount: 0,
    movementPlacedCount: 0,
    passThroughCount: 0
  };
}
function rowValueMovedAsTokens(row) {
  return valueMovedAsTokens(row);
}
function rowPricedUsd(row) {
  const raw = row.value_usd;
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}
function placeMoves(moves, own) {
  const groups = /* @__PURE__ */ new Map();
  for (const move of moves) {
    const from = normalizeAddress(move.from ?? "");
    const to = normalizeAddress(move.to ?? "");
    const fromOurs = own.has(from);
    const toOurs = own.has(to);
    if (fromOurs === toOurs) continue;
    const outbound = fromOurs;
    const counterparty = outbound ? to : from;
    if (!counterparty) continue;
    const key2 = `${outbound ? "out" : "in"}|${move.chainId ?? ""}|${counterparty}`;
    const group = groups.get(key2) ?? { outbound, counterparty, chainId: move.chainId, usd: 0, priced: false };
    if (typeof move.usd === "number" && Number.isFinite(move.usd)) {
      group.usd = (group.usd ?? 0) + move.usd;
      group.priced = true;
    }
    groups.set(key2, group);
  }
  return [...groups.values()].map(({ priced, ...g }) => ({ ...g, usd: priced ? g.usd : null }));
}
function subjectFlowOf(moves, subjectAddresses) {
  const own = new Set([...subjectAddresses].map((a) => normalizeAddress(a)));
  const placements = placeMoves(moves, own);
  if (placements.length === 0) return null;
  const side = (outbound) => {
    const legs = placements.filter((p) => p.outbound === outbound);
    const priced = legs.filter((p) => p.usd !== null);
    return {
      moved: legs.length > 0,
      usd: priced.length > 0 ? priced.reduce((sum, p) => sum + (p.usd ?? 0), 0) : null,
      unpriced: priced.length < legs.length
    };
  };
  const out = side(true);
  const into = side(false);
  return {
    sentUsd: out.usd,
    receivedUsd: into.usd,
    sent: out.moved,
    received: into.moved,
    unpriced: out.unpriced || into.unpriced
  };
}
function valueMovedAsTokens(row) {
  const raw = row.total_value_raw ?? row.value;
  if (raw === void 0 || raw === null || raw === "") return false;
  try {
    return BigInt(String(raw).trim()) === 0n;
  } catch {
    const n = Number(raw);
    return Number.isFinite(n) && n === 0;
  }
}
function fundsFlow(rows, subjectAddresses, options = {}) {
  const opts = typeof options === "function" ? { categoryOf: options } : options;
  const { categoryOf, attributionPending = false, pricedOnly = false } = opts;
  const ruledOut = new Set([...opts.excluded ?? []].map((a) => normalizeAddress(a)).filter(Boolean));
  const own = new Set([...subjectAddresses].map((a) => normalizeAddress(a)).filter(Boolean));
  const bySubjectTransactions = opts.subject === "transactions";
  if (own.size === 0 && !bySubjectTransactions) return emptyFlow();
  const passThrough = /* @__PURE__ */ new Set();
  if (bySubjectTransactions) {
    const senders = /* @__PURE__ */ new Set();
    const recipients = /* @__PURE__ */ new Set();
    for (const row of rows) {
      const from = sideAddress(row, "from");
      const to = sideAddress(row, "to");
      if (from) senders.add(from);
      if (to) recipients.add(to);
    }
    for (const address of senders) if (recipients.has(address)) passThrough.add(address);
  }
  const inflow = /* @__PURE__ */ new Map();
  const outflow = /* @__PURE__ */ new Map();
  const inTxs = /* @__PURE__ */ new Set();
  const outTxs = /* @__PURE__ */ new Set();
  const inUnpricedTxs = /* @__PURE__ */ new Set();
  const outUnpricedTxs = /* @__PURE__ */ new Set();
  let internalCount = 0;
  let excludedCount = 0;
  let droppedUnpricedCount = 0;
  let outsideEndsCount = 0;
  let tokenValueCount = 0;
  let tokenValueUsd = 0;
  let tokenValuePendingCount = 0;
  let movementPlacedCount = 0;
  const record = (outbound, counterparty, usd, chainId, txKey, txId) => {
    const resolved = isZeroAddress(counterparty) ? outbound ? "burned" : "minted" : counterpartyInfrastructureCategory(counterparty, chainId) ?? categoryOf?.(counterparty, chainId) ?? null;
    const category = resolved ?? unresolvedCategory(counterparty, categoryOf !== void 0, attributionPending);
    const bucketed = outbound ? outflow : inflow;
    (outbound ? outTxs : inTxs).add(txKey);
    if (usd === null) {
      (outbound ? outUnpricedTxs : inUnpricedTxs).add(txKey);
    }
    const tally = bucketed.get(category) ?? { usd: 0, transactions: /* @__PURE__ */ new Set(), counterparties: /* @__PURE__ */ new Map() };
    tally.usd += usd ?? 0;
    tally.transactions.add(txId ?? txKey);
    if (counterparty) {
      const seen = tally.counterparties.get(counterparty) ?? { address: counterparty, usd: 0, transactions: /* @__PURE__ */ new Set() };
      seen.usd += usd ?? 0;
      addCounterpartyChain(seen, chainId);
      seen.transactions.add(txId ?? txKey);
      tally.counterparties.set(counterparty, seen);
    }
    bucketed.set(category, tally);
  };
  const seenRows = /* @__PURE__ */ new Set();
  const knownIds = /* @__PURE__ */ new Set();
  for (const [index, r] of rows.entries()) {
    const txId = opts.transactionId?.(r) ?? asTrimmedString(r.id) ?? asTrimmedString(r.transaction_id) ?? void 0;
    const txKey = txId ?? `unidentified-row:${index}`;
    if (seenRows.has(txKey)) continue;
    seenRows.add(txKey);
    if (txId) knownIds.add(txId);
    const from = sideAddress(r, "from");
    const to = sideAddress(r, "to");
    const usd = rowPricedUsd(r);
    if (bySubjectTransactions) {
      if (pricedOnly && usd === null) {
        droppedUnpricedCount += 1;
        continue;
      }
      if (valueMovedAsTokens(r)) {
        tokenValueCount += 1;
        tokenValueUsd += usd ?? 0;
        continue;
      }
      const chainId = normalized(r.chain_id);
      const fromEnd = from !== null && !passThrough.has(from);
      const toEnd = to !== null && !passThrough.has(to);
      if (!fromEnd && !toEnd) {
        if (from || to) internalCount += 1;
        continue;
      }
      if (fromEnd && !ruledOut.has(from)) record(false, from, usd, chainId, txKey, txId);
      else if (fromEnd) excludedCount += 1;
      if (toEnd && !ruledOut.has(to)) record(true, to, usd, chainId, txKey, txId);
      else if (toEnd) excludedCount += 1;
      continue;
    }
    const fromOurs = from !== null && own.has(from);
    const toOurs = to !== null && own.has(to);
    const moves = opts.movesOf?.(r);
    const placements = moves ? placeMoves(moves, own) : [];
    if (placements.length > 0) {
      movementPlacedCount += 1;
      for (const placed of placements) {
        if (ruledOut.has(placed.counterparty)) {
          excludedCount += 1;
          continue;
        }
        if (pricedOnly && placed.usd === null) {
          droppedUnpricedCount += 1;
          continue;
        }
        record(placed.outbound, placed.counterparty, placed.usd, normalized(placed.chainId) ?? normalized(r.chain_id), txKey, txId);
      }
      continue;
    }
    if (valueMovedAsTokens(r)) {
      if (opts.movesOf && moves === void 0) tokenValuePendingCount += 1;
      tokenValueCount += 1;
      tokenValueUsd += usd ?? 0;
      continue;
    }
    if (fromOurs && toOurs) {
      internalCount += 1;
      continue;
    }
    if (!fromOurs && !toOurs) {
      outsideEndsCount += 1;
      continue;
    }
    const outbound = fromOurs;
    const counterparty = outbound ? to : from;
    if (counterparty && ruledOut.has(counterparty)) {
      excludedCount += 1;
      continue;
    }
    if (pricedOnly && usd === null) {
      droppedUnpricedCount += 1;
      continue;
    }
    record(outbound, counterparty, usd, normalized(r.chain_id), txKey, txId);
  }
  const pinned = opts.pinnedCategories ?? [];
  const side = (tallies, unpriced, count2) => {
    const result = buildSide(tallies, unpriced, count2, pinned);
    for (const bucket of result.buckets) {
      bucket.transactionIds = bucket.transactionIds?.filter((id) => knownIds.has(id));
      if (!bucket.transactionIds?.length) delete bucket.transactionIds;
      for (const cp of bucket.top) {
        cp.transactionIds = cp.transactionIds?.filter((id) => knownIds.has(id));
        if (!cp.transactionIds?.length) delete cp.transactionIds;
      }
    }
    return result;
  };
  return {
    source: side(inflow, inUnpricedTxs.size, inTxs.size),
    destination: side(outflow, outUnpricedTxs.size, outTxs.size),
    internalCount,
    excludedCount,
    droppedUnpricedCount,
    outsideEndsCount,
    tokenValueCount,
    tokenValueUsd,
    tokenValuePendingCount,
    movementPlacedCount,
    passThroughCount: passThrough.size
  };
}

// src/funds/stepFlow.ts
var PASS_THROUGH_TOLERANCE = 1e-3;
var EMPTY = Object.freeze({
  source: { buckets: [], usdTotal: 0, unpricedCount: 0, transactions: 0 },
  destination: { buckets: [], usdTotal: 0, unpricedCount: 0, transactions: 0 },
  assets: [],
  hops: [],
  passThrough: 0,
  transit: 0,
  moves: 0,
  transactions: 0,
  unpricedMoves: 0,
  unpricedNftMoves: 0,
  unpricedQuotedElsewhere: 0,
  repriceable: []
});
var NO_PARTIES = /* @__PURE__ */ new Set();
function emptyStepFlow() {
  return EMPTY;
}
function stepFlow(moves, hops = [], options = {}) {
  if (moves.length === 0 && hops.length === 0) return EMPTY;
  const { categoryOf, attributionPending = false } = options;
  const followed = options.followed ?? NO_PARTIES;
  const balances = /* @__PURE__ */ new Map();
  const txs = /* @__PURE__ */ new Set();
  let unpricedMoves = 0;
  let unpricedNftMoves = 0;
  let counted = 0;
  const pricedByAsset = /* @__PURE__ */ new Map();
  const unpricedByAsset = /* @__PURE__ */ new Map();
  const unpricedTxsByAsset = /* @__PURE__ */ new Map();
  const touch = (party, move) => {
    const key2 = `${move.asset.key}|${party}`;
    let b = balances.get(key2);
    if (!b) {
      b = { party, chainId: move.chainId, asset: move.asset, in: 0, out: 0, usdIn: 0, usdOut: 0, unpriced: false, txs: /* @__PURE__ */ new Set() };
      balances.set(key2, b);
    }
    b.txs.add(move.txId);
    return b;
  };
  for (const move of moves) {
    if (move.amount === null || move.amount <= 0 || move.from === move.to) continue;
    counted += 1;
    txs.add(move.txId);
    if (move.usd === null) {
      unpricedMoves += 1;
      if (move.asset.tokenId != null) {
        unpricedNftMoves += 1;
      } else {
        unpricedByAsset.set(move.asset.key, (unpricedByAsset.get(move.asset.key) ?? 0) + 1);
        let unpricedTxs = unpricedTxsByAsset.get(move.asset.key);
        if (!unpricedTxs) unpricedTxsByAsset.set(move.asset.key, unpricedTxs = /* @__PURE__ */ new Set());
        unpricedTxs.add(move.txId);
      }
    } else {
      pricedByAsset.set(move.asset.key, (pricedByAsset.get(move.asset.key) ?? 0) + 1);
    }
    const from = touch(move.from, move);
    from.out += move.amount;
    from.usdOut += move.usd ?? 0;
    if (move.usd === null) from.unpriced = true;
    const to = touch(move.to, move);
    to.in += move.amount;
    to.usdIn += move.usd ?? 0;
    if (move.usd === null) to.unpriced = true;
  }
  const burnedIn = /* @__PURE__ */ new Set();
  const mintedIn = /* @__PURE__ */ new Set();
  for (const move of moves) {
    if (isZeroAddress(move.to)) burnedIn.add(move.txId);
    if (isZeroAddress(move.from)) mintedIn.add(move.txId);
  }
  const bridgeOut = /* @__PURE__ */ new Map();
  const bridgeIn = /* @__PURE__ */ new Map();
  const bridgePartyKey = (party, chain) => JSON.stringify([chain, party]);
  const mark = (into, party, hop) => {
    const key2 = bridgePartyKey(party, hop.localChain);
    const seen = into.get(key2) ?? { chains: /* @__PURE__ */ new Set(), protocol: hop.protocol };
    if (hop.remoteChain) seen.chains.add(hop.remoteChain);
    seen.protocol ??= hop.protocol;
    into.set(key2, seen);
  };
  for (const hop of hops) {
    const into = hop.direction === "out" ? bridgeOut : bridgeIn;
    const zeroMoved = hop.direction === "out" ? burnedIn.has(hop.txId) : mintedIn.has(hop.txId);
    if (zeroMoved) mark(into, EVM_ZERO_ADDRESS, hop);
    if (hop.localParty) mark(into, hop.localParty, hop);
  }
  const categoryFor = (party, chainId, outbound) => {
    if ((outbound ? bridgeOut : bridgeIn).has(bridgePartyKey(party, chainId))) return "bridge";
    if (isZeroAddress(party)) return outbound ? "burned" : "minted";
    if (followed.size > 0 && (followed.has(party) || followed.has(normalizeAddress(party, chainId ?? void 0)))) return "followed";
    if (!party.startsWith("0x") && !isValidAddress(party, chainId)) return "other";
    const resolved = counterpartyInfrastructureCategory(party, chainId) ?? categoryOf?.(party, chainId) ?? null;
    return resolved ?? unresolvedCategory(party, categoryOf !== void 0, attributionPending);
  };
  const inflow = /* @__PURE__ */ new Map();
  const outflow = /* @__PURE__ */ new Map();
  const assetTotals = /* @__PURE__ */ new Map();
  const sourceTxs = /* @__PURE__ */ new Set();
  const destTxs = /* @__PURE__ */ new Set();
  let sourceUnpriced = 0;
  let destUnpriced = 0;
  const passThroughParties = /* @__PURE__ */ new Set();
  const transitSeen = /* @__PURE__ */ new Set();
  for (const b of balances.values()) {
    const { party } = b;
    if (options.transit?.has(party)) {
      transitSeen.add(party);
      continue;
    }
    const net = b.in - b.out;
    const gross = Math.max(b.in, b.out);
    if (b.in > 0 && b.out > 0 && Math.abs(net) <= gross * PASS_THROUGH_TOLERANCE) {
      passThroughParties.add(party);
      continue;
    }
    if (net === 0) continue;
    const isDestination = net > 0;
    const amount = Math.abs(net);
    const usdGross = isDestination ? b.usdIn : b.usdOut;
    const moved = isDestination ? b.in : b.out;
    const usd = b.unpriced ? null : moved > 0 ? usdGross * amount / moved : 0;
    const category = categoryFor(party, b.chainId, isDestination);
    const tallies = isDestination ? outflow : inflow;
    const tally = tallies.get(category) ?? {
      usd: 0,
      transactions: /* @__PURE__ */ new Set(),
      counterparties: /* @__PURE__ */ new Map(),
      assets: /* @__PURE__ */ new Map()
    };
    tally.usd += usd ?? 0;
    for (const t of b.txs) tally.transactions.add(t);
    addAsset(tally.assets, b.asset, amount, usd);
    const cp = tally.counterparties.get(party) ?? {
      address: party,
      usd: 0,
      transactions: /* @__PURE__ */ new Set(),
      assets: /* @__PURE__ */ new Map()
    };
    cp.usd += usd ?? 0;
    addCounterpartyChain(cp, b.chainId);
    for (const t of b.txs) cp.transactions.add(t);
    addAsset(cp.assets, b.asset, amount, usd);
    const bridge = (isDestination ? bridgeOut : bridgeIn).get(bridgePartyKey(party, b.chainId));
    if (bridge) {
      cp.remoteChains ??= /* @__PURE__ */ new Set();
      for (const c of bridge.chains) cp.remoteChains.add(c);
      cp.protocol = bridge.protocol;
    }
    tally.counterparties.set(party, cp);
    tallies.set(category, tally);
    for (const t of b.txs) (isDestination ? destTxs : sourceTxs).add(t);
    if (usd === null) {
      if (isDestination) destUnpriced += 1;
      else sourceUnpriced += 1;
    }
    if (isDestination) {
      const total = assetTotals.get(b.asset.key) ?? { asset: b.asset, amount: 0, usd: 0 };
      total.amount += amount;
      total.usd = total.usd === null || usd === null ? null : total.usd + usd;
      assetTotals.set(b.asset.key, total);
    }
  }
  const pinned = options.pinnedCategories ?? [];
  const quoted = (asset) => (pricedByAsset.get(asset) ?? 0) > 0;
  const repriceable = /* @__PURE__ */ new Set();
  for (const ids of unpricedTxsByAsset.values()) for (const id of ids) repriceable.add(id);
  return {
    source: buildSide(inflow, sourceUnpriced, sourceTxs.size, pinned),
    destination: buildSide(outflow, destUnpriced, destTxs.size, pinned),
    assets: [...assetTotals.values()].sort((a, b) => (b.usd ?? -1) - (a.usd ?? -1) || b.amount - a.amount),
    hops: [...hops],
    passThrough: passThroughParties.size,
    transit: transitSeen.size,
    moves: counted,
    transactions: txs.size,
    unpricedMoves,
    unpricedNftMoves,
    unpricedQuotedElsewhere: [...unpricedByAsset].reduce((sum, [asset, count2]) => sum + (quoted(asset) ? count2 : 0), 0),
    repriceable: [...repriceable]
  };
}
function stepParties(moves) {
  const seen = /* @__PURE__ */ new Map();
  for (const move of moves) {
    for (const party of [move.from, move.to]) {
      if (!party.startsWith("0x") || isZeroAddress(party)) continue;
      const key2 = `${move.chainId ?? ""}::${party}`;
      if (!seen.has(key2)) seen.set(key2, { address: party, chainId: move.chainId });
    }
  }
  return [...seen.values()];
}

// src/funds/asserted.ts
var MAX_ASSERTED_AMOUNTS = 20;
var DECIMAL = /^\d+(\.\d+)?$/;
function isDecimalAmount(text) {
  return DECIMAL.test(text.trim());
}
function assertedValue(amount) {
  if (!isDecimalAmount(amount.amount)) return null;
  const n = Number(amount.amount.trim());
  return Number.isFinite(n) ? n : null;
}
function newAssertedAmount(id, direction = "in") {
  return { id, direction, amount: "", symbol: "", chain: "", source: "" };
}
function stringOr(value) {
  return asString(value) ?? "";
}
function parseAssertedAmounts(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const entry of raw.slice(0, MAX_ASSERTED_AMOUNTS)) {
    if (typeof entry !== "object" || entry === null) continue;
    const row = entry;
    const direction = row.direction === "out" ? "out" : row.direction === "in" ? "in" : null;
    const id = stringOr(row.id).trim();
    if (!id || !direction) continue;
    out.push({
      id,
      direction,
      amount: stringOr(row.amount).trim(),
      symbol: stringOr(row.symbol).trim(),
      chain: stringOr(row.chain).trim(),
      source: stringOr(row.source).trim()
    });
  }
  return out;
}
function savableAssertedAmounts(amounts) {
  return amounts.filter((a) => isDecimalAmount(a.amount)).slice(0, MAX_ASSERTED_AMOUNTS);
}

// src/funds/compare.ts
function symbolKey(symbol) {
  const s = (symbol ?? "").trim().toUpperCase();
  return s || null;
}
function ambiguousSymbols(step) {
  const identities = /* @__PURE__ */ new Map();
  for (const total of step.observed) {
    const symbol = symbolKey(total.asset.symbol);
    if (!symbol || total.amount <= 0) continue;
    const keys = identities.get(symbol) ?? /* @__PURE__ */ new Set();
    keys.add(total.asset.key);
    identities.set(symbol, keys);
  }
  return new Set([...identities].filter(([, keys]) => keys.size > 1).map(([symbol]) => symbol));
}
function amountsBySymbol(step) {
  const out = /* @__PURE__ */ new Map();
  const ambiguous = ambiguousSymbols(step);
  for (const total of step.observed) {
    const key2 = symbolKey(total.asset.symbol);
    if (!key2 || ambiguous.has(key2) || total.amount <= 0) continue;
    const seen = out.get(key2);
    out.set(key2, { amount: (seen?.amount ?? 0) + total.amount, basis: "observed" });
  }
  const asserted = /* @__PURE__ */ new Map();
  for (const a of step.asserted ?? []) {
    const key2 = symbolKey(a.symbol);
    const value = assertedValue(a);
    if (!key2 || value === null || value <= 0) continue;
    const seen = asserted.get(key2) ?? { in: 0, out: 0 };
    seen[a.direction] += value;
    asserted.set(key2, seen);
  }
  for (const [key2, sums] of asserted) {
    if (out.has(key2) || ambiguous.has(key2)) continue;
    out.set(key2, { amount: Math.max(sums.in, sums.out), basis: "asserted" });
  }
  return out;
}
function compareSteps(steps) {
  const bySymbol = steps.map(amountsBySymbol);
  const ambiguous = steps.map(ambiguousSymbols);
  const out = /* @__PURE__ */ new Map();
  steps.forEach((step, index) => {
    const mine = bySymbol[index];
    const comparisons = [];
    for (const [symbol, { amount, basis }] of mine) {
      for (let j = index - 1; j >= 0; j -= 1) {
        if (ambiguous[j].has(symbol)) break;
        const earlier = bySymbol[j].get(symbol);
        if (!earlier || earlier.amount <= 0) continue;
        comparisons.push({
          symbol,
          amount,
          basis,
          ratio: amount / earlier.amount,
          against: { id: steps[j].id, index: j, amount: earlier.amount, basis: earlier.basis }
        });
        break;
      }
    }
    if (comparisons.length > 0) out.set(step.id, comparisons);
  });
  return out;
}
function isNotableRatio(ratio) {
  return ratio > 1.01 || ratio < 0.99;
}
var COMPACT = new Intl.NumberFormat("en-US", { notation: "compact", maximumSignificantDigits: 2 });
function formatRatio(ratio) {
  if (!Number.isFinite(ratio)) return "\xD7\u2014";
  if (ratio >= 1e4) return `\xD7${COMPACT.format(ratio)}`;
  if (ratio >= 100) return `\xD7${Math.round(ratio).toLocaleString()}`;
  if (ratio >= 10) return `\xD7${ratio.toFixed(1)}`;
  if (ratio >= 0.1) return `\xD7${ratio.toFixed(2)}`;
  if (ratio >= 0.01 || ratio <= 0) return `\xD7${ratio.toPrecision(2)}`;
  return `\xF7${COMPACT.format(1 / ratio)}`;
}

// src/funds/phases.ts
function key(label) {
  return label.trim().toLowerCase();
}
function groupPhases(items, phaseOf) {
  const groups = /* @__PURE__ */ new Map();
  items.forEach((item, index) => {
    const label = (phaseOf(item) ?? "").trim();
    if (!label) return;
    const k = key(label);
    const group = groups.get(k) ?? { label, items: [], indices: [] };
    group.items.push(item);
    group.indices.push(index);
    groups.set(k, group);
  });
  return [...groups.values()];
}
function phaseLabels(items, phaseOf) {
  return groupPhases(items, phaseOf).map((g) => g.label);
}

// src/funds/format.ts
var COMPACT2 = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 });
var PLAIN = new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 });
var SMALL = new Intl.NumberFormat("en-US", { maximumSignificantDigits: 4 });
var MIN_DISPLAY_AMOUNT = 1e-4;
function formatAssetAmount(amount) {
  if (!Number.isFinite(amount)) return "\u2014";
  if (amount === 0) return "0";
  const abs = Math.abs(amount);
  if (abs < MIN_DISPLAY_AMOUNT) return amount < 0 ? `>-${MIN_DISPLAY_AMOUNT}` : `<${MIN_DISPLAY_AMOUNT}`;
  if (abs >= 1e6) return COMPACT2.format(amount);
  if (abs >= 1) return PLAIN.format(amount);
  return SMALL.format(amount);
}
function assetSymbol(asset) {
  const symbol = asset.symbol || (asset.tokenAddress ? shortenAddress(asset.tokenAddress) : getChainMetadata(asset.chainId)?.nativeToken ?? NATIVE_TOKEN);
  return asset.tokenId != null ? `${symbol} #${asset.tokenId}` : symbol;
}
function ambiguousAssetSymbols(assets) {
  const bySymbol = /* @__PURE__ */ new Map();
  for (const { asset } of assets) {
    const symbol = assetSymbol(asset);
    const keys = bySymbol.get(symbol) ?? /* @__PURE__ */ new Set();
    keys.add(asset.key);
    bySymbol.set(symbol, keys);
  }
  const ambiguous = /* @__PURE__ */ new Set();
  for (const [symbol, keys] of bySymbol) if (keys.size > 1) ambiguous.add(symbol);
  return ambiguous;
}
function formatAssetAmountWithSymbol(amount) {
  return `${formatAssetAmount(amount.amount)} ${assetSymbol(amount.asset)}`;
}

// src/funds/transfers.ts
function sagaKeyParts(key2) {
  const at = key2.indexOf(":");
  return at < 0 ? { sagaKind: "", value: key2 } : { sagaKind: key2.slice(0, at), value: key2.slice(at + 1) };
}
function citedTransferOf(key2, saga) {
  const { sagaKind, value } = sagaKeyParts(key2);
  const label = `${sagaLabel(saga?.sagaKind ?? sagaKind)} \xB7 ${shortenIdentifier(saga?.value ?? value)}`;
  if (!saga) {
    return { key: key2, label, legsObserved: 0, legsExpected: 0, state: "not in this case's rows", complete: false };
  }
  const observed = Math.max(saga.legs.length, saga.memberCount ?? 0);
  const expected = saga.closed ? observed : Math.max(saga.expectedRoles.length, observed);
  const state = saga.closed ? "closed without delivery" : saga.complete ? "complete" : saga.missingRoles.length > 0 ? `awaiting ${saga.missingRoles.map((r) => titleRole(r).toLowerCase()).join(" + ")}` : saga.openEnded ? "open" : "incomplete";
  return { key: key2, label, legsObserved: observed, legsExpected: expected, state, complete: !saga.closed && saga.complete };
}

// src/funds/pricing.ts
var NO_SCENE_PRICING = Object.freeze({
  valueEdges: 0,
  pricedEdges: 0,
  tokensQuoted: 0,
  priced: false
});
function mergeScenePricing(entries) {
  let valueEdges = 0;
  let pricedEdges = 0;
  let tokensQuoted = 0;
  let priced = false;
  let known = 0;
  let quotesMissing = 0;
  let underlyingEquivalentTokens = 0;
  let limitedBackingTokens = 0;
  for (const entry of entries) {
    if (!entry) continue;
    known += 1;
    valueEdges += entry.valueEdges;
    pricedEdges += entry.pricedEdges;
    tokensQuoted += entry.tokensQuoted;
    quotesMissing += entry.quotesMissing ?? 0;
    underlyingEquivalentTokens += entry.underlyingEquivalentTokens ?? 0;
    limitedBackingTokens += entry.limitedBackingTokens ?? 0;
    priced = priced || entry.priced;
  }
  return {
    valueEdges,
    pricedEdges,
    tokensQuoted,
    priced,
    known,
    quotesMissing,
    ...underlyingEquivalentTokens > 0 ? { underlyingEquivalentTokens, limitedBackingTokens } : {}
  };
}
function unpricedReason(pricing, unpricedMoves, quotedElsewhere = 0, unpricedNftMoves = 0) {
  if (unpricedMoves <= 0) return null;
  const nfts = Math.min(Math.max(unpricedNftMoves, 0), unpricedMoves);
  if (nfts > 0) {
    const explanation = `${nfts} NFT movement${nfts === 1 ? "" : "s"} without an independent valuation. Fungible price backfill does not value NFTs; any observed sale payment is shown separately.`;
    const fungible = unpricedReason(pricing, unpricedMoves - nfts, quotedElsewhere);
    return fungible ? `${fungible} ${explanation}` : explanation;
  }
  const moves = `${unpricedMoves} movement${unpricedMoves === 1 ? "" : "s"}`;
  const missed = Math.min(Math.max(quotedElsewhere, 0), unpricedMoves);
  if (missed > 0 && pricing?.priced) {
    const rest = unpricedMoves - missed;
    const one = missed === 1;
    const lead = !rest ? `${moves} unpriced although the same asset is priced elsewhere in this set` : `${moves} unpriced \u2014 ${missed} of them in an asset priced elsewhere in this set`;
    return `${lead}: the price lookup for ${one ? "that transaction" : "those transactions"} found no quote at ${one ? "its" : "their"} time. A re-run asks the price sources again.` + (rest > 0 ? ` The other ${rest} have no quote for the asset.` : "");
  }
  if (!pricing || pricing.known === 0) {
    return `${moves} unpriced \u2014 in the amounts, not in USD.`;
  }
  if (!pricing.priced) {
    return `${moves} unpriced: the run that processed them did no pricing. Re-run on a fuller pipeline to put dollars on these amounts.`;
  }
  return `${moves} unpriced \u2014 no quote for the asset at that time, so the amount is all there is.`;
}

// src/funds/ledger.ts
function assetLedger(moves, subjects) {
  const own = (party, chain) => subjects.some((s) => (s.chains.length === 0 || s.chains.includes(chain)) && normalizeAddress(s.address, chain) === normalizeAddress(party, chain));
  const seen = /* @__PURE__ */ new Map();
  const conflicts = /* @__PURE__ */ new Set();
  let unresolved = 0;
  for (const move of moves) {
    const chain = move.asset.chainId;
    if (!chain) {
      unresolved++;
      continue;
    }
    if (!own(move.from, chain) && !own(move.to, chain)) continue;
    if (!move.movementId || !move.txId || !move.rawAmount || !/^\d{1,155}$/.test(move.rawAmount)) {
      unresolved++;
      continue;
    }
    const id = JSON.stringify([chain, move.txId, move.movementId]);
    const previous = seen.get(id);
    if (previous && (previous.asset.key !== move.asset.key || BigInt(previous.rawAmount) !== BigInt(move.rawAmount) || previous.from !== move.from || previous.to !== move.to)) conflicts.add(id);
    else seen.set(id, move);
  }
  const totals = /* @__PURE__ */ new Map();
  for (const [id, move] of seen) {
    if (conflicts.has(id)) continue;
    const chain = move.asset.chainId;
    const key2 = JSON.stringify([chain, move.asset.tokenAddress ?? "native"]);
    let row = totals.get(key2);
    if (!row) {
      row = { asset: move.asset, incoming: 0n, outgoing: 0n, internal: 0n, txs: /* @__PURE__ */ new Set(), count: 0 };
      totals.set(key2, row);
    }
    const amount = BigInt(move.rawAmount);
    const from = own(move.from, chain), to = own(move.to, chain);
    if (from && to) row.internal += amount;
    else if (to) row.incoming += amount;
    else if (from) row.outgoing += amount;
    row.txs.add(move.txId);
    row.count++;
  }
  return {
    unresolved,
    conflicting: conflicts.size,
    rows: [...totals.values()].map((r) => ({
      asset: r.asset,
      incomingRaw: String(r.incoming),
      outgoingRaw: String(r.outgoing),
      internalRaw: String(r.internal),
      netRaw: String(r.incoming - r.outgoing),
      transactionIds: [...r.txs].sort(),
      movements: r.count
    })).sort((a, b) => a.asset.key.localeCompare(b.asset.key))
  };
}
function formatRawUnits(raw, decimals) {
  if (!/^-?\d{1,155}$/.test(raw)) return "Unknown";
  if (decimals == null || !Number.isInteger(decimals) || decimals < 0 || decimals > 255) return `${raw} base units`;
  const negative = raw.startsWith("-");
  const digits = raw.replace(/^-/, "").padStart(decimals + 1, "0");
  if (decimals === 0) return `${negative ? "-" : ""}${digits}`;
  const fraction = digits.slice(-decimals).replace(/0+$/, "");
  return `${negative ? "-" : ""}${digits.slice(0, -decimals)}${fraction ? `.${fraction}` : ""}`;
}

export { ALARMING_CATEGORIES, CATEGORY_LABEL, EMPTY_SIDE, MAX_ASSERTED_AMOUNTS, MAX_BUCKET_COUNTERPARTIES, MIN_DISPLAY_AMOUNT, NOTABLE_CATEGORIES, NO_SCENE_PRICING, PASS_THROUGH_TOLERANCE, UNKNOWN_CATEGORIES, addAsset, ambiguousAssetSymbols, amountsBySymbol, assertedValue, assetLedger, assetSymbol, buildSide, categoryLabel, citedTransferOf, compareSteps, counterpartyInfrastructureCategory, emptyStepFlow, flowSelectionIds, formatAssetAmount, formatAssetAmountWithSymbol, formatRatio, formatRawUnits, fundsFlow, groupPhases, isDecimalAmount, isNotableRatio, mergeScenePricing, newAssertedAmount, parseAssertedAmounts, phaseLabels, rowPricedUsd, rowValueMovedAsTokens, sagaKeyParts, savableAssertedAmounts, stepFlow, stepParties, subjectFlowOf, unpricedReason, unresolvedCategory };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map